import type { ChatChoicesDto } from '@dbrain/shared';
import type {
  ActivityDto,
  CatalogueDto,
  ContextDto,
  ConversationDto,
  ConversationRowDto,
  DashboardDto,
  GroupDto,
  InviteDto,
  MeResponse,
  MemberDto,
  MemoryDto,
  NotificationDto,
  PreferencesDto,
  ProjectDto,
  ProviderDto,
  SearchResultDto,
  SessionDto,
  WorkspaceDto,
  WorkspaceMemberDto,
  WorkspaceOverviewDto,
} from '@dbrain/shared';
import { DEFAULT_COMPANY_TIME_ZONE } from '@dbrain/shared';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useCapabilities } from '../experience.js';
import { api, isApiError } from './client.js';

export const keys = {
  me: ['me'] as const,
  dashboard: ['dashboard'] as const,
  activity: ['activity'] as const,
  workspaces: ['workspaces'] as const,
  workspaceOverview: ['workspaces', 'overview'] as const,
  workspace: (id: string) => ['workspace', id] as const,
  workspaceMembers: (id: string) => ['workspace', id, 'members'] as const,
  projects: (workspaceId: string) => ['workspace', workspaceId, 'projects'] as const,
  conversations: (workspaceId: string) => ['workspace', workspaceId, 'conversations'] as const,
  conversation: (id: string) => ['conversation', id] as const,
  sessions: ['sessions'] as const,
  members: ['tenant', 'members'] as const,
  invites: ['tenant', 'invites'] as const,
  tenant: ['tenant'] as const,
  catalogue: (experience: string, view: string, groupId: string | null) => ['catalogue', experience, view, groupId ?? ''] as const,
  catalogueAll: ['catalogue'] as const,
  search: (q: string, experience: string) => ['search', experience, q] as const,
  groups: ['groups'] as const,
  notifications: ['notifications'] as const,
  preferences: ['preferences'] as const,
  memories: ['memories'] as const,
  models: ['models'] as const,
  providers: ['providers'] as const,
  routes: ['model-routes'] as const,
  platform: ['platform'] as const,
  /** kurumsal Yönetim → Modeller: the installation's providers and routes (D-135) */
  adminModels: ['admin', 'models'] as const,
  context: (id: string) => ['conversation', id, 'context'] as const,
  feedback: (id: string) => ['conversation', id, 'feedback'] as const,
};

/** A model as the gateway lists it: window, capabilities, availability and whether members may choose it (MM-006). */
export interface ModelOption {
  id: string;
  providerId: string;
  providerName: string;
  scope: 'platform' | 'tenant' | 'user';
  displayName: string | null;
  modelId: string;
  kind: 'chat' | 'embedding' | 'rerank' | 'ocr' | 'guard';
  contextWindow: { window: number; assumed: boolean };
  contextWindowDetected: number | null;
  contextWindowOverride: number | null;
  maxOutputTokens: number | null;
  thinking: 'unknown' | 'none' | 'template_toggle' | 'effort_toggle' | 'always';
  supportsTools: boolean | null;
  supportsVision: boolean | null;
  enabled: boolean;
  availability: { ok: boolean; reason?: string };
  /** 16c.1: this person may pick it (a picker shows only these) */
  allowed: boolean;
  /** 16c.1: "Sohbette" (an admin opened it); `locked`: a Pro model an Ücretsiz person sees with a lock */
  offered?: boolean;
  locked?: boolean;
  /** the picker's line under the name */
  description?: string | null;
  priceInputMicros: number;
  priceOutputMicros: number;
  missingSince: string | null;
}

export interface RouteResolution {
  modelRef: string;
  via: { scope: 'platform' | 'tenant' | 'user'; callType: string };
}

export interface ModelsResponse {
  models: ModelOption[];
  /** the model each task uses now, and which route chose it */
  routes: Record<string, RouteResolution | null>;
  /** 16c.1: what this person may pick in chat (and in Code), and the model a new chat starts with */
  chat?: ChatChoicesDto;
  code?: ChatChoicesDto;
}

export interface RouteRow {
  scope: 'platform' | 'tenant' | 'user';
  callType: string;
  modelRef: string;
  mine: boolean;
}

/** The signed-in user, or null when signed out (401 is not an error here). */
export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: async () => {
      try {
        return await api<MeResponse>('GET', '/api/me');
      } catch (error) {
        if (isApiError(error, 'unauthenticated')) return null;
        throw error;
      }
    },
    staleTime: 30_000,
  });
}

/** The company's time zone, whose day the pages count: "Bugün", "Dün", "N gün önce" (D-119). */
export function useCompanyZone(): string {
  return useMe().data?.tenant.timeZone ?? DEFAULT_COMPANY_TIME_ZONE;
}

export const useDashboard = () => useQuery({ queryKey: keys.dashboard, queryFn: () => api<DashboardDto>('GET', '/api/dashboard') });

export const useWorkspace = (id: string) =>
  useQuery({ queryKey: keys.workspace(id), queryFn: () => api<WorkspaceDto>('GET', `/api/workspaces/${id}`), enabled: id !== '' });

/** Phase 16a: a person's own account shares no workspace: its members are not asked for */
export function useWorkspaceMembers(id: string) {
  const capabilities = useCapabilities();
  return useQuery({
    queryKey: keys.workspaceMembers(id),
    queryFn: () => api<WorkspaceMemberDto[]>('GET', `/api/workspaces/${id}/members`),
    enabled: capabilities.organization,
  });
}

export const useProjects = (workspaceId: string) =>
  useQuery({ queryKey: keys.projects(workspaceId), queryFn: () => api<ProjectDto[]>('GET', `/api/workspaces/${workspaceId}/projects`) });

export const useConversations = (workspaceId: string) =>
  useQuery({
    queryKey: keys.conversations(workspaceId),
    queryFn: () => api<ConversationRowDto[]>('GET', `/api/workspaces/${workspaceId}/conversations?limit=100`),
  });

/**
 * SA-005: what runs and what waits for the user — the status dots and badges
 * of the lists. Asked again every few seconds while something runs or
 * waits, every half minute otherwise; a conversation page refreshes it
 * when its own run starts or stops.
 */
export const useActivity = () =>
  useQuery({
    queryKey: keys.activity,
    queryFn: () => api<ActivityDto>('GET', '/api/activity'),
    refetchInterval: (query) => (query.state.data && (query.state.data.activeRuns.length > 0 || query.state.data.pendingApprovals.length > 0) ? 5_000 : 30_000),
  });

/** The cards of the workspaces page: what is in each workspace and who is in it. */
export const useWorkspaceOverview = () => useQuery({ queryKey: keys.workspaceOverview, queryFn: () => api<WorkspaceOverviewDto[]>('GET', '/api/workspaces/overview') });

export const useConversation = (id: string) =>
  useQuery({ queryKey: keys.conversation(id), queryFn: () => api<ConversationDto>('GET', `/api/conversations/${id}`) });

export const useSessions = () => useQuery({ queryKey: keys.sessions, queryFn: () => api<SessionDto[]>('GET', '/api/sessions') });

export function useMembers() {
  const capabilities = useCapabilities();
  return useQuery({ queryKey: keys.members, queryFn: () => api<MemberDto[]>('GET', '/api/tenant/members'), enabled: capabilities.organization });
}

export const useInvites = (enabled: boolean) =>
  useQuery({ queryKey: keys.invites, queryFn: () => api<InviteDto[]>('GET', '/api/tenant/invites'), enabled });

/** QW-201, D-20: the session list in pages; pinned ones on the first page. */
export const useCatalogue = (experience: string, view: 'active' | 'archived' | 'trash', groupId: string | null) =>
  useInfiniteQuery({
    queryKey: keys.catalogue(experience, view, groupId),
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      api<CatalogueDto>(
        'GET',
        `/api/conversations?experience=${experience}&view=${view}&limit=30${groupId ? `&groupId=${groupId}` : ''}${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });

export const useSearch = (q: string, experience: string) =>
  useQuery({
    queryKey: keys.search(q, experience),
    queryFn: () => api<SearchResultDto[]>('GET', `/api/conversations/search?q=${encodeURIComponent(q)}&experience=${experience}`),
    enabled: q.trim().length >= 2,
  });

export const useGroups = () => useQuery({ queryKey: keys.groups, queryFn: () => api<GroupDto[]>('GET', '/api/conversation-groups') });

export const useNotifications = () =>
  useQuery({
    queryKey: keys.notifications,
    queryFn: () => api<{ items: NotificationDto[]; unread: number }>('GET', '/api/notifications'),
    refetchInterval: 30_000,
  });

export const usePreferences = () => useQuery({ queryKey: keys.preferences, queryFn: () => api<PreferencesDto>('GET', '/api/me/preferences') });

export const useMemories = () => useQuery({ queryKey: keys.memories, queryFn: () => api<MemoryDto[]>('GET', '/api/memories') });

export const useModels = () => useQuery({ queryKey: keys.models, queryFn: () => api<ModelsResponse>('GET', '/api/models'), staleTime: 60_000 });
export const useProviders = () => useQuery({ queryKey: keys.providers, queryFn: () => api<ProviderDto[]>('GET', '/api/providers') });
export const useRoutes = () => useQuery({ queryKey: keys.routes, queryFn: () => api<RouteRow[]>('GET', '/api/model-routes') });

export const useContext = (conversationId: string, enabled: boolean) =>
  useQuery({ queryKey: keys.context(conversationId), queryFn: () => api<ContextDto>('GET', `/api/conversations/${conversationId}/context`), enabled });

export const useFeedback = (conversationId: string) =>
  useQuery({
    queryKey: keys.feedback(conversationId),
    queryFn: () => api<{ messageId: string; rating: number; note: string | null }[]>('GET', `/api/conversations/${conversationId}/feedback`),
  });

export interface TenantSettings {
  id: string;
  name: string;
  kind: 'personal' | 'organization';
  allowFullAccess: boolean;
  memoryAllowed: boolean;
  requireCodeApproval: boolean;
  trashRetentionDays: number | null;
  /** the zone for people whose browser does not tell and who chose none (Phase 12) */
  defaultTimeZone: string;
  /** D-58: words + meaning, or words only */
  documentSearch: 'hybrid' | 'keyword';
  /** MM-006: the chat models members may choose; null: all */
  modelAllowlist: string[] | null;
  /** MM-022: members may add their own providers */
  allowPersonalProviders: boolean;
  /** set by the platform operator (Phase 7 decision) */
  allowInternalProviderUrls: boolean;
  role: string;
}

export const useTenant = () => useQuery({ queryKey: keys.tenant, queryFn: () => api<TenantSettings>('GET', '/api/tenant') });
